import React, { FC, useContext } from "react";
import {
  Box,
  Card,
  CardActions,
  CardContent,
  Grid,
  LinearProgress,
  Typography,
} from "@mui/material";

import { GrantVestment } from "../../types/grant-vestment";
import { useNavigate } from "react-router-dom";
import { getPercentage, moneyFormat } from "../../utils/money-format";
import { numberOfDays, shortFormat } from "../../utils/date-format";
import imagePlaceholder from "../../assets/image-placeholader.png";
import GrantVestmentInteractions from "./grant-vestment-details/grant-vestment-actions";
import { AuthContext } from "providers/userProvider";
import { AuthData } from "types/auth";
import { ImageDisplay } from "./Grantvestment-image";

interface GrantVestmentsCardProps {
  grantvestment: GrantVestment;
  single: Boolean;
  allContent: boolean;
  promoting?: boolean;
}

const GrantVestmentsCard: FC<GrantVestmentsCardProps> = ({
  grantvestment,
  single,
  allContent,
  promoting,
}) => {
  const { userData } = useContext<AuthData>(AuthContext);

  const navigate = useNavigate();
  const funded = getPercentage(
    grantvestment?.raisedAmount,
    grantvestment?.goalAmount
  );
  const daysLeft =
    numberOfDays(grantvestment?.deadline) < 0
      ? 0
      : numberOfDays(grantvestment?.deadline);
  const handleClick = (
    event: React.MouseEvent<HTMLSpanElement, MouseEvent>
  ) => {
    navigate(`/grantvestments/${grantvestment?.name}`, {
      state: { id: grantvestment?.grantVestmentId },
    });
  };
  const getContent = () => {
    return (
      <>
        <CardContent>
          <Typography
            variant="smallCopy"
            color="neutral.main"
            className="title"
            gutterBottom
          >
            {grantvestment?.category?.categoryName}
          </Typography>
          <Box sx={{ height: single ? "auto" : "180px" }}>
            <Typography
              onClick={(event) => handleClick(event)}
              sx={{
                color: "neutral.main",
                "&:hover": { cursor: "pointer" },
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: "2",
                WebkitBoxOrient: "vertical",
                whiteSpace: "pre-wrap",
              }}
              variant="h3"
              gutterBottom
            >
              {grantvestment?.name}
            </Typography>
            <Typography
              variant="bodyCopyRegular"
              sx={{
                color: "neutral.lighter",
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: "3",
                WebkitBoxOrient: "vertical",
                whiteSpace: "pre-wrap",
              }}
              paragraph
            >
              {grantvestment?.description}
            </Typography>
          </Box>
          {!promoting && (
            <>
              {allContent ? (
                <>
                  <Typography
                    variant="smallCopy"
                    className="title"
                    sx={{ color: "neutral.lighter" }}
                    paragraph
                  >
                    Raised
                  </Typography>
                  <Typography
                    variant="bodyCopyBold"
                    sx={{ color: "neutral.main" }}
                    gutterBottom
                  >
                    ${moneyFormat(grantvestment?.raisedAmount)}{" "}
                    <Typography variant="smallCopyBold">
                      out of ${moneyFormat(grantvestment?.goalAmount)} goal
                    </Typography>
                  </Typography>
                  <Box sx={{ width: "100%", my: 2 }}>
                    <LinearProgress
                      color="primary"
                      variant="determinate"
                      value={parseFloat(funded) > 100 ? 100 : parseInt(funded)}
                    />
                  </Box>
                  <Typography
                    variant="smallCopy"
                    color={"primary"}
                    className="title"
                    paragraph
                  >
                    {funded}% funded
                  </Typography>
                </>
              ) : (
                <>
                  <Typography
                    variant="smallCopy"
                    color={
                      grantvestment?.status !== "PROMOTING"
                        ? "primary"
                        : "white"
                    }
                    className="title"
                    paragraph
                  >
                    {funded}% funded
                  </Typography>
                  <Box sx={{ width: "100%", my: 2 }}>
                    <LinearProgress
                      color="primary"
                      sx={{
                        backgroundColor:
                          grantvestment?.status !== "PROMOTING"
                            ? "primary"
                            : "white",
                      }}
                      variant="determinate"
                      value={parseFloat(funded) > 100 ? 100 : parseInt(funded)}
                    />
                  </Box>
                </>
              )}
              <Typography
                variant="smallCopy"
                className="title"
                sx={{
                  color:
                    grantvestment?.status !== "PROMOTING"
                      ? "neutral.lighter"
                      : "white",
                }}
                paragraph
              >
                {grantvestment?.status === "RAISING" ? (
                  <>{daysLeft} days left</>
                ) : (
                  <>closed on {shortFormat(grantvestment?.closedAt)} </>
                )}
              </Typography>
            </>
          )}
        </CardContent>
        <CardActions disableSpacing>
          <Grid
            container
            justifyContent="space-between"
            alignItems={"center"}
            sx={{
              "@media (min-width: 900px) and (max-width: 1400px)": {
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
              },
            }}
          >
            <Grid item>
              <Typography
                variant="bodyCopyBold"
                sx={{
                  color: "neutral.main",
                  wordBreak: "break-word",
                  hyphens: "auto",
                  overflowWrap: "break-word",
                }}
              >
                By {grantvestment?.projectBy}
              </Typography>
            </Grid>
            <Grid item>
              {userData?.user && grantvestment && (
                <div onClick={(event) => event.stopPropagation()}>
                  <GrantVestmentInteractions
                    grantvestment={grantvestment}
                    user={userData?.user}
                  />
                </div>
              )}
            </Grid>
          </Grid>
        </CardActions>
      </>
    );
  };
  return single ? (
    <>
      <Card>
        <Box sx={{ height: "200px", width: "100%" }}>
          <ImageDisplay
            imageUrl={grantvestment.imageUrl}
            placeholderUrl={imagePlaceholder}
            alt="GrantVestment image"
            width="100%"
            sx={{
              height: "100%",
              aspectRatio: "auto",
            }}
          />
        </Box>

        <Box sx={{ m: 3, width: "-webkit-fill-available", flex: 3 }}>
          {getContent()}
        </Box>
      </Card>
    </>
  ) : (
    <Card className="list" onClick={(event) => handleClick(event)}>
      <Box sx={{ height: "200px", width: "100%" }}>
        <ImageDisplay
          imageUrl={grantvestment.imageUrl}
          placeholderUrl={imagePlaceholder}
          alt="GrantVestment picture"
          width="100%"
          sx={{
            height: "100%",
            aspectRatio: "auto",
          }}
        />
      </Box>

      <Box
        sx={{
          m: 3,
          width: "-webkit-fill-available",
          flex: 3,
        }}
      >
        {getContent()}
      </Box>
    </Card>
  );
};

export default GrantVestmentsCard;
